import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  useTheme,
  useMediaQuery,
  ListItemButton,
  Link,
} from "@mui/material";
import {
  ArrowBack,
  Email,
  Phone,
  LocationOn,
  Business,
  Person,
  Receipt,
  Assignment,
  Handshake,
  Engineering,
  Visibility,
  Description,
  Euro,
  CalendarToday,
  AttachFile,
  OpenInNew,
  ElectricBolt,
  Home,
  AddLocationAlt,
  SwapHoriz,
  SolarPower,
  EvStation,
  AccountBalance,
} from "@mui/icons-material";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import {
  CLIENT_TYPES,
  ENTRY_CHANNEL_LABELS,
  ENTRY_CHANNELS,
  CLIENT_TAGS,
  CLIENT_TAG_LABELS,
} from "../../utils/models/clientModel";
import { PROJECT_STATUS_LABELS } from "../../utils/models/projectModel";

const ClientDetail = ({ clientId, onEdit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { document: client, loading } = useDocument("clients", clientId);
  const { data: invoices } = useCollection("invoices");
  const { data: projects } = useCollection("projects");
  const { data: contacts } = useCollection("contacts");
  const [activeTab, setActiveTab] = useState(0);

  if (loading) return <LoadingSpinner />;
  if (!client) return <EmptyState title="Cliente no encontrado" />;

  const addresses =
    client.addresses && client.addresses.length > 0
      ? client.addresses
      : [
          {
            street: client.address?.street || "",
            city: client.address?.city || "",
            state: client.address?.state || "",
            zipCode: client.address?.zipCode || "",
            country: client.address?.country || "España",
            cups: client.cups || "",
            referenciaCatastral: client.referenciaCatastral || "",
            notes: "",
            hasInvoice: client.hasInvoice || false,
            invoiceLink: client.invoiceLink || "",
          },
        ];

  const clientInvoices =
    invoices?.filter((i) => i.clientId === clientId && i.isActive !== false) ||
    [];
  const clientProjects =
    projects?.filter((p) => p.clientId === clientId && p.isActive !== false) ||
    [];

  const sortedInvoices = [...clientInvoices].sort(
    (a, b) =>
      new Date(b.createdAt || b.date || 0) -
      new Date(a.createdAt || a.date || 0),
  );
  const sortedProjects = [...clientProjects].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
  );

  const isCompany = client.type === CLIENT_TYPES.COMPANY;
  const displayName = isCompany
    ? client.companyName
    : `${client.firstName || ""} ${client.lastName || ""}`.trim();
  const hasAnyAddress = addresses.some((addr) => addr.street || addr.city);

  const channelContact =
    client.entryChannelContactId && contacts
      ? contacts.find((c) => c.id === client.entryChannelContactId)
      : null;
  const showChannelContact =
    (client.entryChannel === ENTRY_CHANNELS.COMMERCIAL ||
      client.entryChannel === ENTRY_CHANNELS.EPC) &&
    channelContact;

  // Verificar si alguna dirección tiene factura
  const hasAnyInvoice = addresses.some(
    (addr) => addr.hasInvoice === true || addr.hasInvoice === "true",
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "—";
    return `${Number(amount).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
  };

  const getInvoiceStatusLabel = (status) => {
    const labels = {
      draft: "Borrador",
      sent: "Enviada",
      paid: "Pagada",
      overdue: "Vencida",
      cancelled: "Cancelada",
    };
    return labels[status] || status || "—";
  };

  const getInvoiceStatusColor = (status) => {
    const colors = {
      draft: "default",
      sent: "info",
      paid: "success",
      overdue: "error",
      cancelled: "warning",
    };
    return colors[status] || "default";
  };

  const getTagIcon = (tag) => {
    switch (tag) {
      case CLIENT_TAGS.CAMBIO_COMERCIALIZADORA:
        return <SwapHoriz sx={{ fontSize: { xs: 12, sm: 14 } }} />;
      case CLIENT_TAGS.FOTOVOLTAICA:
        return <SolarPower sx={{ fontSize: { xs: 12, sm: 14 } }} />;
      case CLIENT_TAGS.CARGADOR_ELECTRICO:
        return <EvStation sx={{ fontSize: { xs: 12, sm: 14 } }} />;
      default:
        return null;
    }
  };

  const getTagColor = (tag) => {
    switch (tag) {
      case CLIENT_TAGS.CAMBIO_COMERCIALIZADORA:
        return "warning";
      case CLIENT_TAGS.FOTOVOLTAICA:
        return "success";
      case CLIENT_TAGS.CARGADOR_ELECTRICO:
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.12)"
            : "rgba(0, 95, 101, 0.12)",
        background: theme.palette.background.paper,
        overflow: "hidden",
      }}
    >
      {/* CABECERA */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 1, sm: 2 },
          mb: { xs: 3, sm: 4 },
          flexWrap: "wrap",
        }}
      >
        <IconButton
          onClick={() => navigate("/clients")}
          size="small"
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
            mt: 0.5,
            "&:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(0, 175, 163, 0.1)"
                  : "rgba(0, 95, 101, 0.05)",
            },
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              wordBreak: "break-word",
            }}
          >
            {displayName}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <Chip
              icon={
                isCompany ? (
                  <Business sx={{ fontSize: { xs: 14, sm: 16 } }} />
                ) : (
                  <Person sx={{ fontSize: { xs: 14, sm: 16 } }} />
                )
              }
              label={isCompany ? "Empresa" : "Particular"}
              size="small"
              color={isCompany ? "secondary" : "primary"}
              variant="outlined"
              sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            />
            {client.entryChannel &&
              ENTRY_CHANNEL_LABELS?.[client.entryChannel] && (
                <Chip
                  label={`Canal: ${ENTRY_CHANNEL_LABELS[client.entryChannel]}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    borderColor: theme.palette.info.main,
                    color: theme.palette.info.main,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  }}
                />
              )}
            {showChannelContact && (
              <Chip
                icon={
                  client.entryChannel === ENTRY_CHANNELS.COMMERCIAL ? (
                    <Handshake sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  ) : (
                    <Engineering sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  )
                }
                label={`${client.entryChannel === ENTRY_CHANNELS.COMMERCIAL ? "Comercial: " : "EPC: "}${channelContact.firstName} ${channelContact.lastName}`}
                size="small"
                color="info"
                variant="outlined"
                sx={{
                  borderColor: theme.palette.info.main,
                  color: theme.palette.info.main,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              />
            )}
            {hasAnyInvoice && (
              <Chip
                icon={<ElectricBolt sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                label="Con factura"
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              />
            )}
            {addresses.length > 1 && (
              <Chip
                icon={<AddLocationAlt sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                label={`${addresses.length} direcciones`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.main,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              />
            )}
            {/* TAGS EN EL DETALLE */}
            {client.tags?.map((tag) => (
              <Chip
                key={tag}
                icon={getTagIcon(tag)}
                label={CLIENT_TAG_LABELS[tag] || tag}
                size="small"
                color={getTagColor(tag)}
                variant="outlined"
                sx={{
                  borderColor: theme.palette[getTagColor(tag)]?.main || theme.palette.primary.main,
                  color: theme.palette[getTagColor(tag)]?.main || theme.palette.primary.main,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          width: "100%",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* INFORMACIÓN - LADO IZQUIERDO */}
        <Box
          sx={{
            flex: { md: "1 1 340px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "300px" },
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  borderBottom: "2px solid",
                  borderColor: theme.palette.primary.main,
                  pb: 1,
                  display: "inline-block",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Información de contacto
              </Typography>

              <List dense disablePadding>
                {/* DATOS PERSONA FÍSICA */}
                {!isCompany && client.firstName && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Person
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Nombre"
                      secondary={`${client.firstName || ""} ${client.lastName || ""}`.trim()}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {!isCompany && client.dni && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Assignment
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.info.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="DNI/NIF"
                      secondary={client.dni}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {client.contactName && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Person
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.secondary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Persona de contacto"
                      secondary={client.contactName}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {/* DATOS EMPRESA */}
                {isCompany && client.companyName && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Business
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.secondary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Razón social"
                      secondary={client.companyName}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {isCompany && client.taxId && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Assignment
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.info.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="CIF/NIF"
                      secondary={client.taxId}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {isCompany && client.representativeId && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Assignment
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.warning.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="ID Representante"
                      secondary={client.representativeId}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {client.email && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Email
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={client.email}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                        wordBreak: "break-all",
                      }}
                    />
                  </ListItem>
                )}
                {client.phone && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Phone
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Teléfono"
                      secondary={client.phone}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}
                {client.iban && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AccountBalance
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.info.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="IBAN"
                      secondary={client.iban}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                        wordBreak: "break-all",
                      }}
                    />
                  </ListItem>
                )}

                {/* DIRECCIONES CON FACTURA INTEGRADA */}
                {hasAnyAddress && (
                  <>
                    <Divider
                      sx={{ my: 2, borderColor: theme.palette.divider }}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        mb: 2,
                        color: theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: { xs: "0.85rem", sm: "0.9rem" },
                      }}
                    >
                      <LocationOn sx={{ fontSize: { xs: 18, sm: 20 } }} />
                      {addresses.length > 1
                        ? `Direcciones (${addresses.length})`
                        : "Dirección"}
                    </Typography>

                    {addresses.map((address, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: index < addresses.length - 1 ? 2 : 0,
                          p: 1.5,
                          borderRadius: "12px",
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.02)"
                              : "rgba(0, 95, 101, 0.02)",
                        }}
                      >
                        {addresses.length > 1 && (
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color="primary.main"
                            sx={{
                              fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              mb: 0.5,
                              display: "block",
                            }}
                          >
                            Dirección {index + 1}
                          </Typography>
                        )}

                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                            mb:
                              address.cups || address.referenciaCatastral
                                ? 1
                                : 0,
                          }}
                        >
                          {[
                            address.street,
                            address.city,
                            address.state,
                            address.zipCode,
                            address.country !== "España"
                              ? address.country
                              : null,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </Typography>

                                                {(address.cups || address.cupsGas || address.referenciaCatastral) && (
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            {address.cups && (
                              <Chip
                                icon={<ElectricBolt sx={{ fontSize: 14 }} />}
                                label={`CUPS Luz: ${address.cups}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                  borderColor: theme.palette.warning.main,
                                  color: theme.palette.warning.main,
                                }}
                              />
                            )}
                            {address.cupsGas && (
                              <Chip
                                icon={
                                  <span style={{ fontSize: 14 }}>🔥</span>
                                }
                                label={`CUPS Gas: ${address.cupsGas}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                  borderColor: theme.palette.error.main,
                                  color: theme.palette.error.main,
                                }}
                              />
                            )}
                            {address.referenciaCatastral && (
                              <Chip
                                icon={<Home sx={{ fontSize: 14 }} />}
                                label={`Ref: ${address.referenciaCatastral}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                  borderColor: theme.palette.secondary.main,
                                  color: theme.palette.secondary.main,
                                }}
                              />
                            )}
                          </Box>
                        )}

                        {address.notes && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              mt: 0.5,
                              display: "block",
                              fontStyle: "italic",
                            }}
                          >
                            📝 {address.notes}
                          </Typography>
                        )}

                        {/* FACTURA DE ESTA DIRECCIÓN */}
                        {(address.hasInvoice === true ||
                          address.hasInvoice === "true") && (
                          <Box
                            sx={{
                              mt: 1.5,
                              pt: 1.5,
                              borderTop: "1px dashed",
                              borderColor: theme.palette.divider,
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{
                                color: theme.palette.primary.main,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mb: 1,
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              }}
                            >
                              <ElectricBolt sx={{ fontSize: 14 }} /> Factura de
                              luz
                            </Typography>
                            {address.invoiceLink ? (
                              <Button
                                variant="outlined"
                                size="small"
                                component="a"
                                href={address.invoiceLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary"
                                fullWidth
                                startIcon={<OpenInNew />}
                                sx={{
                                  borderRadius: "10px",
                                  textTransform: "none",
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                }}
                              >
                                Ver factura
                              </Button>
                            ) : (
                              <Chip
                                label="Pendiente de enlace"
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{
                                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </>
                )}

                {showChannelContact && (
                  <>
                    <Divider
                      sx={{ my: 2, borderColor: theme.palette.divider }}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        mb: 1,
                        color: theme.palette.info.main,
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      }}
                    >
                      {client.entryChannel === ENTRY_CHANNELS.COMMERCIAL
                        ? "Comercial asignado"
                        : "EPC asignado"}
                    </Typography>
                    {channelContact.email && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Email
                            sx={{
                              fontSize: { xs: 18, sm: 20 },
                              color: theme.palette.info.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Email"
                          secondary={channelContact.email}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                            wordBreak: "break-all",
                          }}
                        />
                      </ListItem>
                    )}
                    {channelContact.phone && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Phone
                            sx={{
                              fontSize: { xs: 18, sm: 20 },
                              color: theme.palette.info.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Teléfono"
                          secondary={channelContact.phone}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                          }}
                        />
                      </ListItem>
                    )}
                  </>
                )}

                {isCompany && client.representativeName && (
                  <>
                    <Divider
                      sx={{ my: 2, borderColor: theme.palette.divider }}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        mb: 1,
                        color: theme.palette.secondary.main,
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      }}
                    >
                      Representante legal
                    </Typography>

                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Person
                          sx={{
                            fontSize: { xs: 18, sm: 20 },
                            color: theme.palette.secondary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Nombre"
                        secondary={client.representativeName}
                        primaryTypographyProps={{
                          variant: "caption",
                          color: "text.secondary",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 600,
                          fontSize: { xs: "0.8rem", sm: "0.85rem" },
                        }}
                      />
                    </ListItem>

                    {client.representativeDni && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Assignment
                            sx={{
                              fontSize: { xs: 18, sm: 20 },
                              color: theme.palette.info.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="DNI/NIF"
                          secondary={client.representativeDni}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                          }}
                        />
                      </ListItem>
                    )}

                    {client.representativePhone && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Phone
                            sx={{
                              fontSize: { xs: 18, sm: 20 },
                              color: theme.palette.success.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Teléfono"
                          secondary={client.representativePhone}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                          }}
                        />
                      </ListItem>
                    )}

                    {client.representativeEmail && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Email
                            sx={{
                              fontSize: { xs: 18, sm: 20 },
                              color: theme.palette.warning.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Email"
                          secondary={client.representativeEmail}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                            wordBreak: "break-all",
                          }}
                        />
                      </ListItem>
                    )}
                  </>
                )}

                {/* METADATA */}
                <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    mb: 1,
                    color: theme.palette.text.secondary,
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  }}
                >
                  Información del sistema
                </Typography>

                {client.createdAt && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CalendarToday
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.text.secondary,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Fecha de alta"
                      secondary={formatDate(client.createdAt)}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {client.assignedTo && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Person
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.text.secondary,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Asignado a"
                      secondary={client.assignedTo}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Visibility
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: client.isActive !== false
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Estado"
                    secondary={client.isActive !== false ? "Activo" : "Inactivo"}
                    primaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      color:
                        client.isActive !== false
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                    }}
                  />
                </ListItem>
              </List>

              {/* ARCHIVOS */}
              {client.files && client.files.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      mb: 2,
                      color: theme.palette.primary.main,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    }}
                  >
                    <AttachFile sx={{ fontSize: { xs: 18, sm: 20 } }} />{" "}
                    Archivos adjuntos
                  </Typography>
                  <List dense disablePadding>
                    {client.files.map((file, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Description
                            sx={{
                              fontSize: { xs: 18, sm: 20 },
                              color: theme.palette.info.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "pointer",
                              wordBreak: "break-all",
                              "&:hover": {
                                color: theme.palette.secondary.main,
                              },
                            }}
                          >
                            {file.name || file.url}{" "}
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        </ListItemText>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {client.notes && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Notas
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {client.notes}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* FACTURAS Y PROYECTOS - LADO DERECHO */}
        <Box
          sx={{
            flex: { md: "2 1 540px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "350px" },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                px: { xs: 1, sm: 2 },
                pt: 2,
                borderBottom: "1px solid",
                borderColor: theme.palette.divider,
                "& .MuiTab-root": {
                  color: theme.palette.text.secondary,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  minWidth: { xs: "auto", sm: 90 },
                  px: { xs: 1, sm: 2 },
                  "&.Mui-selected": { color: theme.palette.primary.main },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              <Tab
                label={`Facturas (${clientInvoices.length})`}
                icon={<Receipt sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                iconPosition="start"
              />
              <Tab
                label={`Proyectos (${clientProjects.length})`}
                icon={<Assignment sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                iconPosition="start"
              />
            </Tabs>

            {activeTab === 0 &&
              (sortedInvoices.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <EmptyState
                    title="Sin facturas"
                    description="Este cliente no tiene facturas aún"
                    compact
                  />
                </Box>
              ) : (
                <List dense disablePadding>
                  {sortedInvoices.map((invoice) => (
                    <ListItem
                      key={invoice.id}
                      disablePadding
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: theme.palette.divider,
                      }}
                    >
                      <ListItemButton
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 1.5, sm: 2 },
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.03)"
                                : "rgba(0, 95, 101, 0.03)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                          <Receipt
                            sx={{
                              fontSize: { xs: 20, sm: 24 },
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                              }}
                            >
                              {invoice.invoiceNumber ||
                                `Factura #${invoice.id?.substring(0, 8)}`}
                            </Typography>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CalendarToday sx={{ fontSize: 12 }} />
                                {formatDate(invoice.date || invoice.createdAt)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Euro sx={{ fontSize: 12 }} />
                                {formatCurrency(invoice.total)}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            label={getInvoiceStatusLabel(invoice.status)}
                            size="small"
                            color={getInvoiceStatusColor(invoice.status)}
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              height: { xs: 22, sm: 24 },
                            }}
                          />
                          <Visibility
                            sx={{
                              fontSize: { xs: 16, sm: 20 },
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ))}

            {activeTab === 1 &&
              (sortedProjects.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <EmptyState
                    title="Sin proyectos"
                    description="Este cliente no tiene proyectos aún"
                    compact
                  />
                </Box>
              ) : (
                <List dense disablePadding>
                  {sortedProjects.map((project) => (
                    <ListItem
                      key={project.id}
                      disablePadding
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: theme.palette.divider,
                      }}
                    >
                      <ListItemButton
                        onClick={() => navigate(`/projects/${project.id}`)}
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 1.5, sm: 2 },
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.03)"
                                : "rgba(0, 95, 101, 0.03)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                          <Assignment
                            sx={{
                              fontSize: { xs: 20, sm: 24 },
                              color: theme.palette.secondary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                              }}
                            >
                              {project.name || "Sin nombre"}
                            </Typography>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                }}
                              >
                                {project.address?.street
                                  ? `${project.address.street}, ${project.address.city || ""}`
                                  : "Sin dirección"}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            label={
                              PROJECT_STATUS_LABELS[project.status] ||
                              project.status ||
                              "—"
                            }
                            size="small"
                            color={
                              project.status === "completed"
                                ? "success"
                                : project.status === "in_progress"
                                  ? "primary"
                                  : "default"
                            }
                            variant="outlined"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              height: { xs: 22, sm: 24 },
                            }}
                          />
                          <Visibility
                            sx={{
                              fontSize: { xs: 16, sm: 20 },
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ))}
          </Card>
        </Box>
      </Box>
    </Paper>
  );
};

export default ClientDetail;